/**
 * Redux Store Configuration
 *
 * This file configures the central Redux store for the application.
 * All application state is managed through this store.
 *
 * Store Structure:
 * The store combines all reducers from src/datastage/store/all-reducers.js
 * Each feature module has its own slice of state.
 *
 * Available State Slices:
 * - auth        - Authentication state (user, token, permissions)
 * - products    - Products data and filters
 * - jobs        - Job processing state
 * - dateMath    - Date math calculations
 * - rights      - Rights management
 * - users       - User administration
 * - (see all-reducers.js for complete list)
 *
 * Usage in Components:
 * ```
 * import { useSelector, useDispatch } from 'react-redux';
 *
 * // Read state
 * const { products } = useSelector(state => state.products);
 *
 * // Dispatch actions
 * const dispatch = useDispatch();
 * dispatch(someAction(payload));
 * ```
 *
 * @see src/datastage/store/all-reducers.js - All reducer definitions
 * @see src/datastage/store/[feature]/ - Individual feature stores
 */
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { datastageReducers } from '../../datastage/store/all-reducers';

// Combine all feature reducers into a single app reducer
const appReducer = combineReducers({
    ...datastageReducers,
});

/**
 * Root Reducer
 *
 * Wraps the app reducer to handle special actions like state reset.
 * When 'RESET_REDUCERS' is dispatched (e.g., on logout),
 * all state is cleared except auth to prevent stale data.
 *
 * @param {Object} state - Current Redux state
 * @param {Object} action - Dispatched action
 * @returns {Object} New state
 */
const rootReducer = (state, action) => {
    // Reset all state except auth on RESET_REDUCERS action
    if (action.type === 'RESET_REDUCERS') {
        const { auth } = state;
        state = { auth };
    }

    return appReducer(state, action);
};

// Create and export the configured Redux store
export const store = configureStore({
    reducer: rootReducer,
});

export default store;
